/**
 * V37.42.0 — Cold-Start Warm-Up Hook
 *
 * Fire-and-forget GET /api/warmup to prime the FastAPI worker's lazy-loaded
 * heavy modules (brain_box, intent classifiers, file_intelligence, etc.)
 * BEFORE the user submits a prompt. Eliminates the 90s K8s-cold-start
 * pattern reported on idle/fork.
 *
 * Triggers:
 *   - On mount (initial app load)
 *   - On window focus
 *   - On document visibilitychange → 'visible'
 *
 * Throttled to once per WARMUP_THROTTLE_MS to avoid hammering the backend.
 * Uses `keepalive: true` so the request survives navigation. All errors are
 * swallowed — warmup is a best-effort optimization.
 *
 * Constraints honored:
 *   - Non-blocking (no await on the user's path)
 *   - No effect on streaming, finalization, routing, or watchdog
 *   - Additive only
 */
import { useEffect, useRef } from 'react';
import { getBackendUrl } from './apiUrl';

const WARMUP_THROTTLE_MS = 60_000; // re-fire at most once per minute

export function useColdStartWarmup() {
  const lastFiredRef = useRef(0);

  useEffect(() => {
    const BACKEND_URL = getBackendUrl();
    if (!BACKEND_URL) return;

    const fire = () => {
      const now = Date.now();
      if (now - lastFiredRef.current < WARMUP_THROTTLE_MS) return;
      lastFiredRef.current = now;
      try {
        // keepalive lets the request finish even if the page navigates.
        fetch(`${BACKEND_URL}/api/warmup`, {
          method: 'GET',
          credentials: 'include',
          keepalive: true,
        }).catch(() => { /* silent */ });
      } catch { /* silent */ }
    };

    // (1) Mount fire — primes the worker before the user types.
    fire();

    // (2) Re-fire on window focus / tab return.
    const onFocus = () => fire();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fire();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
}

export default useColdStartWarmup;
